import { css } from '@emotion/react'
import { useSiteCategories } from 'api/goodtrust/site'
import { Button } from 'components/button/Button'
import { useModal } from 'components/modal/Modal'
import { OrderRequestModal } from 'components/order/orderRequestModal/OrderRequestModal'
import { OrderSiteList } from 'components/order/orderSiteList/OrderSiteList'
import { PageTitle } from 'components/PageTitle'
import { SideLayout } from 'components/sideLayout/SideLayout'
import { Sides } from 'components/Sides'
import { StoreCart } from 'components/store/storeCart/StoreCart'
import { StoreContent, StoreWrapper } from 'components/store/storeContent/StoreContent'
import { Text, Title, Title_h2_default } from 'components/Typography'
import { WizardProgressHeader } from 'components/wizard/wizardProgressHeader/WizardProgressHeader'
import { WizardSearchField } from 'components/wizard/wizardSearchField/WizardSearchField'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { useEffect, useState } from 'react'
import { fireEvent, mutateAnalyticsData } from 'utils/analytics'
import { getStoreCartTotal, removeCart, useStoreCart } from 'utils/cart'
import { BREAKPOINTS } from 'utils/styled'
import { GET_STORE_WIZARD_HEADER } from 'utils/constants'

export default function OrderSelectSites(props: { continueHref: string; siteHref: string }) {
  const { t } = useTranslation('device')
  const cart = useStoreCart()
  const { data: sitesData } = useSiteCategories()
  const [search, setSearch] = useState('')

  useEffect(() => {
    fireEvent('wgs_started')
  }, [])

  const [requestModal, showRequestModal] = useModal(({ close, isOpen }) =>
    isOpen ? <OrderRequestModal onClose={close} /> : null
  )

  let sitesExists = true
  if (search) {
    sitesExists = false
    Object.values(sitesData?.json ?? {}).find((value) => {
      if (
        value?.sites?.find((site) =>
          site?.title?.toLocaleLowerCase()?.includes(search.toLocaleLowerCase())
        )
      ) {
        sitesExists = true
      }
      return sitesExists
    })
  }

  let title: string | null = null
  let description: string | null = null

  if (sitesExists) {
    if (cart.items && Object.values(cart.items).length > 0) {
      title = t('device.store.sites_exists_cart_has_items_title')
      description = null
    } else {
      title = t('device.store.sites_exists_cart_no_items_title')
      description = t('device.store.sites_exists_cart_no_items_desc')
    }
  } else {
    title = t('device.store.no_site_title')
    description = t('device.store.no_site_desc')
  }

  return (
    <SideLayout
      header={
        <WizardProgressHeader
          steps={GET_STORE_WIZARD_HEADER()}
          currentStep={GET_STORE_WIZARD_HEADER()[0]?.value}
        />
      }
      rightCss={{ boxShadow: 'var(--card-shadow)' }}
      right={
        <StoreCart>
          {cart.items && Object.values(cart.items).length > 0 && (
            <Button
              type="button"
              href={props.continueHref}
              size="large"
              onClick={() => {
                mutateAnalyticsData({
                  price: getStoreCartTotal(cart).total / 100,
                  currency: 'USD',
                })
                fireEvent('wgs_sites_selected')
              }}
              data-testid="store-site-submit"
            >
              {t('device.store.rhs_cart_button')}
            </Button>
          )}
        </StoreCart>
      }
    >
      <PageTitle title={t('device.store.meta_title')} />
      {requestModal}

      <StoreWrapper>
        <StoreContent>
          <Sides
            css={css`
              align-items: flex-start;
              @media ${BREAKPOINTS.LG.max} {
                flex-direction: column;
              }
            `}
          >
            <div>
              <Title variant={Title_h2_default} as="h2" css={{ marginBottom: '2rem' }}>
                {title}
              </Title>

              {description && <Text css={{ marginBottom: '2rem' }}>{description}</Text>}
            </div>
            <WizardSearchField
              value={search}
              onChange={setSearch}
              css={css`
                margin-top: 1rem;
                margin-left: 3rem;
                flex-basis: 350px;
                flex-shrink: 0;
                margin-bottom: 2rem;

                @media ${BREAKPOINTS.LG.max} {
                  margin-left: 0;
                  width: 100%;
                  margin-top: 0;
                  flex-basis: unset;
                }
              `}
            />
          </Sides>

          {sitesExists ? (
            <OrderSiteList
              search={search}
              values={
                cart.items
                  ? [...new Set(Object.values(cart.items).map((item) => item.siteUuid))]
                  : []
              }
              onSelect={({ uuid }) => Router.push(`${props.siteHref}${uuid}`)}
              onRemove={(id) => removeCart(id)}
            />
          ) : (
            <Button type="button" size="large" onClick={showRequestModal}>
              {t('device.store.need_help_button')}
            </Button>
          )}
        </StoreContent>
      </StoreWrapper>
    </SideLayout>
  )
}
